import { FeatureEntity } from '@gishub/domain';

/**
 * Inversiones locales de Chupaca (Junín) cargadas de forma manual (GeoJSON local)
 * mientras el servicio MapServer/6 no exponga estas inversiones.
 * Los atributos están normalizados a los campos ArcGIS que consume la app.
 */

export const INTERVENCIONES_SECTORIALES_LAYER_NAME = 'Intervenciones sectoriales en comunidades';

/**
 * Iconos (PictureMarkerSymbol PNG) que usa la capa MapServer/6 en su leyenda
 * (uniqueValue renderer sobre tipo_intervencion). imageData obtenido del endpoint
 * /6?f=json de Monitoreo_Territorial_Junin.
 */
export const LOCAL_INTERVENTION_ICONS: Record<string, string> = {
  'Proyecto de Inversión':
    'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAA8AAAAbCAYAAACjkdXHAAAAAXNSR0IB2cksfwAAAAlwSFlzAAAOxAAADsQBlSsOGwAAAbRJREFUOI2t1L9rU1EYxvHve8+1R5DiD6ztDbp00Yp2KvRfEDo5NRiCugj9AzoJ4uai4OZcpCXgInTp6uYk1KWLFkuSm2ClNakdDvGe16GpCW3SE64+07lwPud5DwduzD8k/n94qTp+Zd/M7BluEsk1MrUYWnjZQdliLfl6AmtEqblMpA9oc2fPEAHgFQTwAN11Od0lY50Oz3lXqMYs1S7QNk/wTAfn9EwgPGaM90A15s2Ng6TYuN8w+gkwIW9VXrm1ZP3vnRuV5DOldANYCFh1Pnvdd+duIj7gg3ibyvX6aYzUQc+mEbX+zx7ONEMCvZ5sMBamAhTg8mCszAabYRpUQPRk8/wIzRcpN2/xlq0eflSfpMPtETCgC9CPO1KEEYYG8CwCL4/ww2/n+S3LwWfqZc4W03uuUtiIKdjM1nTFeeaASxbGXcRVPBPdzQco+1Y4dPAT+OLOyc5R84uk4+Dp8bHueFFKn1lk0a1O3QXxjtMZ/jNQNp3RGRA/bMtQbI1+d5n8GHr4Wdh5WtbQHjRuEDMW/XJOD3M146WN5MUrky3Kzd18GFF8+jEnBlYL2/lxIH8AjmOVp9KtBKoAAAAASUVORK5CYII=',
  Actividad:
    'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAA8AAAAYCAYAAAAlBadpAAAAAXNSR0IB2cksfwAAAAlwSFlzAAAOxAAADsQBlSsOGwAAAdFJREFUOI2l0j9oE2EYx/Hv895dL9wJkkk3JxU3BV1UOoh1d2hAS6iCo1TXNiVIkXZwULoFQYdyHQzOOrgIKggiCCJRaRAH8c+QBnJp0vbex0GNtrlLID7b+8DnfX/Pw+vyH+XubjxsXPE3/f0HEk3yInjGmobj+l8KuXIjFa+2545ZZFrQcx2Vw6gawYCCFbBJlyguNYE3wJNtvMp0eOO7+6CzkN9Kui8EciCDUu4FxoFxl62vwF23kCs3VuO564qcAQpDxnyqSG1fEN7vxb4YLlaAShSXjgKHsqRV51Jxz8Kn1IWp8Eg0E3/4F/ZhgzxX9FqaVOTZ7t4OLCrvFU19VtB3A7Fqt4l4Ganlx0CcGH/MqE2nYvs+1I6Go3YiPTRYNaeAe304as8fV9WrCuczLIJejuJ5D+zrqXDxzi+sCG1uC5zOgj2PFkEurLRnHxeDpZpb3ZgJN9EukADOkAss8NlRcxCouZPBcgs4iyLVjZmwY3zXWAlQrwqcROSEJvptzPNbeb/cnBB6G/27MEEnWW79Pq1HcektcGQquPkqK0bf+v+UImuCfhw0QyZGbB2VETFaB0bDarbrJvFHw8XcrfWVePblSBigGC6tjYyH1U+GfKt1hL1fywAAAABJRU5ErkJggg==',
};

export const LOCAL_INTERVENTION_IMAGES: string[] = [
  'assets/chupaca/1.jpg',
  'assets/chupaca/2.jpg',
  'assets/chupaca/3.jpg',
  'assets/chupaca/4.webp',
  'assets/chupaca/5.jpg',
];

interface LocalInterventionSource {
  id: number;
  attributes: Record<string, any>;
  coordinates: [number, number];
  imagen?: string;
}

const LOCAL_INTERVENTION_SOURCES: LocalInterventionSource[] = [
  {
    id: 1,
    coordinates: [-75.4355, -12.1895],
    attributes: {
      objectid: -1,
      cui: '2516185',
      nombre_intervencion: 'Mejoramiento del servicio de recarga hídrica y aprovechamiento de agua de lluvia en 14 departamentos',
      dependencia_responsable: 'AGRORURAL',
      tipo_intervencion: 'Proyecto de Inversión',
      servicio_publico: 'Agricultura y Riego',
      fase_pi: 'VIABLE',
      presupuesto: 416712773.2,
      beneficiarios: 8200,
      region: 'Junín',
      provincia: 'Chupaca',
      distrito: 'Yanacancha',
      ubigeo: '120909',
      estado: 'ACTIVO',
      fecha_inicio: '2021-04-09',
      avance_pct: 50.44,
    },
  },
  {
    id: 2,
    coordinates: [-75.472, -12.153],
    attributes: {
      objectid: -2,
      cui: '2686477',
      nombre_intervencion: 'Creación del servicio de provisión de agua para riego en las comunidades de Shicuy y Santa Cruz de Ranra',
      dependencia_responsable: 'PSI',
      tipo_intervencion: 'Proyecto de Inversión',
      servicio_publico: 'Agricultura y Riego',
      fase_pi: 'VIABLE',
      presupuesto: 10784215.32,
      beneficiarios: 170,
      region: 'Junín',
      provincia: 'Chupaca',
      distrito: 'San Juan de Jarpa',
      ubigeo: '120907',
      estado: 'ACTIVO',
      fecha_inicio: '2025-03-27',
      avance_pct: null,
    },
  },
  {
    id: 3,
    coordinates: [-75.4315, -12.187],
    attributes: {
      objectid: -3,
      cui: '2650049',
      nombre_intervencion: 'Construcción de captación de agua en recarga hídrica Canal de Riego Pampas de Laive-Ingahuasi',
      dependencia_responsable: 'Fondo Sierra Azul',
      tipo_intervencion: 'Proyecto de Inversión',
      servicio_publico: 'Agricultura y Riego',
      fase_pi: 'APROBADO',
      presupuesto: 709606.58,
      beneficiarios: null,
      region: 'Junín',
      provincia: 'Chupaca',
      distrito: 'Yanacancha',
      ubigeo: '120909',
      estado: 'ACTIVO',
      fecha_inicio: '2024-07-30',
      avance_pct: 98.26,
    },
  },
  {
    id: 4,
    coordinates: [-75.4388, -12.1922],
    attributes: {
      objectid: -4,
      cui: '2650375',
      nombre_intervencion: 'Construcción de captación de agua en recarga hídrica Irrigación de Pamparca',
      dependencia_responsable: 'Fondo Sierra Azul',
      tipo_intervencion: 'Proyecto de Inversión',
      servicio_publico: 'Agricultura y Riego',
      fase_pi: 'APROBADO',
      presupuesto: 726371.19,
      beneficiarios: null,
      region: 'Junín',
      provincia: 'Chupaca',
      distrito: 'Yanacancha',
      ubigeo: '120909',
      estado: 'ACTIVO',
      fecha_inicio: '2024-07-30',
      avance_pct: 98.28,
    },
  },
  {
    id: 5,
    coordinates: [-75.4332, -12.195],
    attributes: {
      objectid: -5,
      cui: '2650369',
      nombre_intervencion: 'Construcción de captación de agua en recarga hídrica Sistema de Riego Yacuy Unión Ambo',
      dependencia_responsable: 'Fondo Sierra Azul',
      tipo_intervencion: 'Proyecto de Inversión',
      servicio_publico: 'Agricultura y Riego',
      fase_pi: 'APROBADO',
      presupuesto: 482801.27,
      beneficiarios: null,
      region: 'Junín',
      provincia: 'Chupaca',
      distrito: 'Yanacancha',
      ubigeo: '120909',
      estado: 'ACTIVO',
      fecha_inicio: '2024-07-30',
      avance_pct: 98.06,
    },
  },
  {
    id: 6,
    coordinates: [-75.56544045363894, -12.288560513229505],
    imagen: 'assets/chupaca2/1.jpeg',
    attributes: {
      objectid: -6,
      cui: '2650049',
      nombre_intervencion: 'Construcción de captación de agua en recarga hídrica Canal de Riego Pampas de Laive-Ingahuasi',
      dependencia_responsable: 'MINAGRI - FONDO SIERRA AZUL',
      tipo_intervencion: 'Proyecto de Inversión',
      fase_pi: 'APROBADO',
      presupuesto: 709606.58,
      beneficiarios: null,
      pim: 12330,
      avance_pct: 98.26,
      meta: '102.1 HA',
      situacion_inversion: '[12/08/2026] OBRA CULMINADA. TRANSCURRIDA LA TEMPORADA DE LLUVIAS, SE REALIZARÁ LA VERIFICACIÓN TÉCNICA EN CAMPO, A TRAVÉS DE LA CONTRATACIÓN DE PROFESIONALES, A FIN DE CONSTATAR LA OPERATIVIDAD DE LAS OBRAS CONSTRUIDAS, Y QUE ÉSTAS SEAN TRANSFERIDAS EN LAS CONDICIONES ADECUADAS Y OPTIMAS AL BENEFICIARIO FINAL.',
      region: 'Junín',
      provincia: 'Chupaca',
      distrito: 'Yanacancha',
      centro_poblado: '',
      ubigeo: '120909',
      estado: 'ACTIVO',
    },
  },
  {
    id: 7,
    coordinates: [-75.3878203619005, -12.2027301960355],
    imagen: 'assets/chupaca2/2.jpeg',
    attributes: {
      objectid: -7,
      cui: '2650375',
      nombre_intervencion: 'Construcción de captación de agua en recarga hídrica Irrigación de Pamparca',
      dependencia_responsable: 'MINAGRI - FONDO SIERRA AZUL',
      tipo_intervencion: 'Proyecto de Inversión',
      fase_pi: 'APROBADO',
      presupuesto: 726371.19,
      beneficiarios: null,
      pim: 12480,
      avance_pct: 98.28,
      meta: '158 HA',
      situacion_inversion: '[12/08/2026] OBRA CULMINADA. TRANSCURRIDA LA TEMPORADA DE LLUVIAS, SE REALIZARÁ LA VERIFICACIÓN TÉCNICA EN CAMPO, A TRAVÉS DE LA CONTRATACIÓN DE PROFESIONALES, A FIN DE CONSTATAR LA OPERATIVIDAD DE LAS OBRAS CONSTRUIDAS, Y QUE ÉSTAS SEAN TRANSFERIDAS EN LAS CONDICIONES ADECUADAS Y OPTIMAS AL BENEFICIARIO FINAL.',
      region: 'Junín',
      provincia: 'Chupaca',
      distrito: 'Yanacancha',
      centro_poblado: '',
      ubigeo: '120909',
      estado: 'ACTIVO',
    },
  },
  {
    id: 8,
    coordinates: [-75.5517999692065, -12.352991124831883],
    imagen: 'assets/chupaca2/3.jpeg',
    attributes: {
      objectid: -8,
      cui: '2650369',
      nombre_intervencion: 'Construcción de captación de agua en recarga hídrica Sistema de Riego Yacuy Unión Ambo',
      dependencia_responsable: 'MINAGRI - FONDO SIERRA AZUL',
      tipo_intervencion: 'Proyecto de Inversión',
      fase_pi: 'APROBADO',
      presupuesto: 482801.27,
      beneficiarios: null,
      pim: 9380,
      avance_pct: 98.06,
      meta: '16 HA',
      situacion_inversion: '[12/08/2026] OBRA CULMINADA. TRANSCURRIDA LA TEMPORADA DE LLUVIAS, SE REALIZARÁ LA VERIFICACIÓN TÉCNICA EN CAMPO, A TRAVÉS DE LA CONTRATACIÓN DE PROFESIONALES, A FIN DE CONSTATAR LA OPERATIVIDAD DE LAS OBRAS CONSTRUIDAS, Y QUE ÉSTAS SEAN TRANSFERIDAS EN LAS CONDICIONES ADECUADAS Y OPTIMAS AL BENEFICIARIO FINAL.',
      region: 'Junín',
      provincia: 'Chupaca',
      distrito: 'Yanacancha',
      centro_poblado: '',
      ubigeo: '120909',
      estado: 'ACTIVO',
    },
  },
  {
    id: 9,
    coordinates: [-75.3428739833811, -12.0203069452246],
    imagen: 'assets/chupaca2/4.jpg',
    attributes: {
      objectid: -9,
      cui: '2726201',
      nombre_intervencion: 'Mejoramiento del servicio de distribución de energía eléctrica en líneas y redes primarias de media tensión, subestaciones de distribución, redes secundarias de baja tensión y alumbrado público en el alimentador A4407 distrito de Huachac, provincia de Chupaca, departamento de Junín',
      dependencia_responsable: 'ELECTROCENTRO',
      tipo_intervencion: 'Proyecto de Inversión',
      fase_pi: 'VIABLE',
      presupuesto: 17830920.75,
      beneficiarios: 18280,
      pim: 0,
      avance_pct: null,
      meta: null,
      descr: 'Implementación del proyecto: MEJORAMIENTO DEL SERVICIO DE DISTRIBUCIÓN DE ENERGÍA ELÉCTRICA EN ALIMENTADOR A4407, DISTRITO DE CHUPACA DE LA PROVINCIA DE CHUPACA DEL DEPARTAMENTO DE JUNIN; beneficiando a 14 localidades con la instalación de líneas primarias aéreas convencionales con sistema Trifásico en 13.2 kV de Operación Inicial proyectado para un nivel de tensión en 22,9 kV; a lo largo de una longitud total de 33.88 km con conductores de Aleación de aluminio desnudo de 70 mm2, se cambiarán las redes primarias a lo largo de una longitud total de 69.95 km con conductor de Aleación aluminio de 70 mm2, 51 subestaciones de distribución de doble relación 13.2-22,9 kV, también se instalarán redes secundarias Trifásicas con Conductor Autoportante de Aluminio 45.18 km de redes, 1312 Postes de C.A.C de 9m, el cual atienden 1194 acometidas y 531 luminarias con tecnología LED de 55W.',
      region: 'Junín',
      provincia: 'Chupaca',
      distrito: 'Huachac',
      centro_poblado: '',
      ubigeo: '120904',
      estado: 'ACTIVO',
    },
  },
];

/**
 * Mezcla determinística (misma imagen por id en cada sesión) de las imágenes
 * disponibles entre las intervenciones locales ("al azar" pero estable).
 */
function shuffledImages(): string[] {
  const imgs = [...LOCAL_INTERVENTION_IMAGES];
  const out: string[] = [];
  let seed = 20260814;
  while (imgs.length > 0) {
    seed = (seed * 9301 + 49297) % 233280;
    const idx = Math.floor((seed / 233280) * imgs.length);
    out.push(imgs.splice(idx, 1)[0]);
  }
  return out;
}

export function buildLocalInterventions(): FeatureEntity[] {
  const images = shuffledImages();
  return LOCAL_INTERVENTION_SOURCES.map((src, i) => ({
    attributes: {
      ...src.attributes,
      _local: true,
      _imagenes: [src.imagen ?? images[i % images.length]],
    },
    geometry: {
      type: 'Point',
      coordinates: src.coordinates,
      spatialReference: { wkid: 4326 },
    },
  }));
}